import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Receipt, Boxes, Wallet, Globe, FileText, Truck, Inbox, PackageSearch, Building2, Pill, Factory,
  Clock, Wrench, Target, BarChart3, Check, ArrowRight, X, ChevronRight, MessageCircle, ShieldCheck, Zap, Smartphone, Lock,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../lib/api';

const FONT_URL = 'https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300..800&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap';
const fmt = (n) => `R$ ${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const MODULES = [
  { icon: ShoppingCart, title: 'PDV rapido', desc: 'Frente de caixa para balcao e celular, codigo de barras, Pix e varias formas de pagamento.', tag: 'Vendas' },
  { icon: Receipt, title: 'Vendas e pedidos', desc: 'Orcamentos, pedidos, comissao por vendedor, impressao de cupom e pedido duplo.', tag: 'Vendas' },
  { icon: Boxes, title: 'Estoque por filial', desc: 'Inventario, transferencias entre lojas, movimentacoes e trilha de auditoria.', tag: 'Operacao' },
  { icon: Wallet, title: 'Financeiro e DRE', desc: 'Contas a pagar/receber, caixa, plano de contas, conciliacao e DRE realizado.', tag: 'Financeiro' },
  { icon: Globe, title: 'Catalogo digital', desc: 'Sua vitrine online com link proprio, pedidos pelo WhatsApp e tabelas de preco.', tag: 'Online' },
  { icon: FileText, title: 'NF-e e NFC-e', desc: 'Emissao direta na SEFAZ, carta de correcao, cancelamento e DANFE.', tag: 'Fiscal' },
  { icon: Truck, title: 'MDF-e', desc: 'Manifesto de transporte para suas entregas e cargas.', tag: 'Fiscal' },
  { icon: Inbox, title: 'NF de entrada (Manifesto)', desc: 'Receba as notas emitidas contra seu CNPJ e de entrada com um clique.', tag: 'Fiscal' },
  { icon: PackageSearch, title: 'Compras', desc: 'Pedidos de compra, importacao por XML e requisicoes entre filiais.', tag: 'Operacao' },
  { icon: Building2, title: 'Multi-filial', desc: 'Varias lojas em uma conta, com estoque, caixa e relatorios por filial.', tag: 'Gestao' },
  { icon: Pill, title: 'Farmacia', desc: 'SNGPC, medicamentos controlados, CMED e receituario.', tag: 'Add-on' },
  { icon: Factory, title: 'Producao', desc: 'Ordens de producao, ficha tecnica, perdas e quebras.', tag: 'Add-on' },
  { icon: Clock, title: 'Ponto eletronico', desc: 'Registro de ponto inteligente e relatorio de horas da equipe.', tag: 'Add-on' },
  { icon: Wrench, title: 'Ordens de servico', desc: 'OS, locacao, status e historico por cliente.', tag: 'Add-on' },
  { icon: Target, title: 'Metas e cashback', desc: 'Metas por vendedor e programa de cashback para fidelizar.', tag: 'Add-on' },
  { icon: BarChart3, title: 'Relatorios gerenciais', desc: 'Lucro direto, curva ABC, vendas por periodo e muito mais.', tag: 'Gestao' },
];

const FAQ = [
  ['Preciso instalar algo?', 'Nao. O 8IP roda no navegador do computador, tablet e celular (PWA). Seus dados ficam na nuvem com backup.'],
  ['Posso trocar de plano depois?', 'Sim. O upgrade e feito pela nossa equipe sem perder nenhum dado — o tipo de negocio do plano define os modulos liberados.'],
  ['Como funciona a filial adicional?', `Cada loja extra custa ${fmt(129)}/mes e ganha estoque, caixa e relatorios proprios dentro da mesma conta.`],
  ['A emissao fiscal e homologada?', 'Sim. Emitimos NF-e, NFC-e e MDF-e direto na SEFAZ com seu certificado A1.'],
];

const Logo = ({ branding, dark = true }) => (
  <div className="flex items-center gap-3" data-testid="landing-logo">
    {branding?.logo_url ? (
      <img src={branding.logo_url} alt={branding.name} className="h-10 w-10 rounded-xl object-contain bg-white/90 p-1" />
    ) : (
      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-600/30">
        <span className="text-white font-black text-lg" style={{ fontFamily: 'Plus Jakarta Sans' }}>8</span>
      </div>
    )}
    <div className="leading-tight">
      <p className={`font-extrabold text-lg ${dark ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Plus Jakarta Sans' }}>{branding?.name || '8IP'} <span className="text-violet-400">ERP</span></p>
      <p className={`text-[10px] uppercase tracking-[0.2em] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>Gestao e Fiscal</p>
    </div>
  </div>
);

export const LandingPage = () => {
  const [plans, setPlans] = useState([]);
  const [branding, setBranding] = useState(null);
  const [register, setRegister] = useState(null); // { plan, addons }
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [branches, setBranches] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!document.getElementById('landing-fonts')) {
      const l = document.createElement('link'); l.id = 'landing-fonts'; l.rel = 'stylesheet'; l.href = FONT_URL; document.head.appendChild(l);
    }
    axios.get(`${API}/public/plans`).then(({ data }) => setPlans(data.data || [])).catch(() => {});
    axios.get(`${API}/platform/branding`).then(({ data }) => setBranding(data)).catch(() => {});
  }, []);

  const mainPlans = useMemo(() => plans.filter(p => (p.kind || 'plan') === 'plan'), [plans]);
  const addons = useMemo(() => plans.filter(p => p.kind === 'addon'), [plans]);
  const branchPrice = mainPlans[0]?.branch_price ?? 129;
  const addonTotal = selectedAddons.reduce((s, id) => s + (Number(addons.find(a => a.id === id)?.price) || 0), 0);
  const toggleAddon = (id) => setSelectedAddons(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  const consultor = branding?.whatsapp ? `https://wa.me/${String(branding.whatsapp).replace(/\D/g, '')}` : '#contato';

  return (
    <div className="min-h-screen bg-[#0f0f23] text-slate-100" style={{ fontFamily: 'DM Sans, sans-serif' }} data-testid="landing-page">
      {/* NAV */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0f0f23]/80 border-b border-violet-500/10">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo branding={branding} />
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-300">
            <a href="#modulos" className="hover:text-white transition-colors">Modulos</a>
            <a href="#planos" className="hover:text-white transition-colors">Planos</a>
            <a href="#como-funciona" className="hover:text-white transition-colors">Como funciona</a>
            <a href="#faq" className="hover:text-white transition-colors">Duvidas</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="px-4 py-2 text-sm font-semibold text-slate-200 hover:text-white" data-testid="nav-login">Entrar</Link>
            <a href="#planos" className="px-4 py-2 rounded-full bg-violet-600 hover:bg-violet-500 text-white text-sm font-bold transition-colors shadow-lg shadow-violet-600/30" data-testid="nav-cta">Comecar gratis</a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-violet-600/20 blur-[120px]" />
          <div className="absolute top-20 right-0 w-[420px] h-[420px] rounded-full bg-indigo-600/20 blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-200 text-xs font-semibold uppercase tracking-[0.18em]" data-testid="hero-badge">
              <ShieldCheck size={14} /> ERP + Fiscal + Catalogo em um so lugar
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]" style={{ fontFamily: 'Plus Jakarta Sans' }} data-testid="hero-title">
              Gestao completa para a sua empresa <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-indigo-300">vender mais e sem dor de cabeca fiscal</span>.
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-300 max-w-2xl">
              PDV, estoque por filial, financeiro com DRE, catalogo digital com WhatsApp e emissao de NF-e, NFC-e e MDF-e direto na SEFAZ. Tudo no navegador, no celular ou no balcao.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#planos" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-violet-600 hover:bg-violet-500 text-white font-bold transition-all shadow-xl shadow-violet-600/30" data-testid="hero-cta-plans">
                Ver planos e comecar gratis <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a href={consultor} target={consultor.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-slate-600 hover:border-violet-400 text-slate-100 font-semibold transition-colors" data-testid="hero-cta-consultor">
                <MessageCircle size={18} /> Falar com consultor
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[['7 dias', 'gratis para testar'], ['SEFAZ', 'NF-e, NFC-e e MDF-e'], ['Multi-loja', 'filiais na mesma conta']].map(([a, b]) => (
                <div key={a} className="border-l-2 border-violet-500/60 pl-3">
                  <p className="text-xl font-extrabold text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>{a}</p>
                  <p className="text-xs text-slate-400">{b}</p>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="lg:col-span-5">
            <div className="relative rounded-3xl border border-violet-500/20 bg-[#16162a] p-6 shadow-2xl shadow-violet-900/40">
              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Painel do dia</p>
              <p className="mt-2 text-3xl font-extrabold text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>R$ 18.420,00</p>
              <p className="text-xs text-emerald-400 font-semibold">+12% vs ontem · 3 filiais</p>
              <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                {[['PDV', '142 vendas'], ['NFC-e', '138 autorizadas'], ['Estoque', '2 alertas'], ['Caixa', 'fechado 18:02']].map(([k, v]) => (
                  <div key={k} className="rounded-xl bg-[#0f0f23] border border-slate-800 p-3">
                    <p className="text-[11px] text-slate-500">{k}</p>
                    <p className="font-bold text-slate-100">{v}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-center gap-2 text-xs text-slate-400"><Smartphone size={14} /> Funciona no celular, tablet e computador</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MODULOS */}
      <section id="modulos" className="bg-[#f8fafc] text-slate-900 py-24">
        <div className="max-w-7xl mx-auto px-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-600">Tudo o que o 8IP faz</p>
          <h2 className="mt-3 text-base md:text-lg font-semibold text-slate-600 max-w-2xl">Modulos pensados para o dia a dia do comercio, servicos, industria e farmacia — contrate so o que precisa.</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="modules-grid">
            {MODULES.map((m, i) => (
              <motion.div key={m.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.06 }}
                className="group rounded-2xl bg-white border border-slate-200 p-5 hover:border-violet-400 hover:shadow-lg hover:shadow-violet-200/60 transition-all" data-testid={`module-card-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="h-10 w-10 rounded-xl bg-violet-50 text-violet-700 flex items-center justify-center group-hover:bg-violet-600 group-hover:text-white transition-colors"><m.icon size={20} /></div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${m.tag === 'Add-on' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{m.tag}</span>
                </div>
                <p className="mt-4 font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>{m.title}</p>
                <p className="mt-1 text-sm text-slate-600">{m.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANOS */}
      <section id="planos" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-400">Planos</p>
          <h2 className="mt-3 text-base md:text-lg font-semibold text-slate-300 max-w-2xl">Comece pelo essencial e evolua: cada plano ja vem com o tipo de negocio e as permissoes certas. Add-ons e filiais podem ser adicionados a qualquer plano.</h2>
          <div className="mt-10 grid md:grid-cols-2 xl:grid-cols-4 gap-5 items-stretch" data-testid="plans-grid">
            {mainPlans.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className={`relative flex flex-col rounded-3xl p-6 border ${p.highlight ? 'bg-gradient-to-b from-violet-600/20 to-[#16162a] border-violet-500/60 shadow-2xl shadow-violet-700/30 xl:-translate-y-3' : 'bg-[#16162a] border-slate-800 hover:border-violet-500/40'} transition-all`} data-testid={`plan-card-${p.id}`}>
                {p.highlight && <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-violet-500 text-white text-[10px] font-black uppercase tracking-wider">Mais escolhido</span>}
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Plano {i + 1}</p>
                <h3 className="mt-1 text-xl font-extrabold text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>{p.name}</h3>
                <p className="mt-1 text-sm text-slate-400 min-h-[40px]">{p.description}</p>
                <p className="mt-4"><span className="text-4xl font-extrabold text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>{fmt(p.price)}</span><span className="text-slate-400 text-sm">/mes</span></p>
                <p className="text-[11px] text-slate-500">+ {fmt(p.branch_price ?? branchPrice)} por filial adicional</p>
                <ul className="mt-5 space-y-2 text-sm text-slate-300 flex-1">
                  {(p.features || []).map(f => <li key={f} className="flex gap-2"><Check size={16} className="text-violet-400 flex-shrink-0 mt-0.5" />{f}</li>)}
                </ul>
                <button onClick={() => setRegister({ plan: p })} className={`mt-6 w-full py-3 rounded-full font-bold transition-colors ${p.highlight ? 'bg-violet-600 hover:bg-violet-500 text-white' : 'bg-white/5 hover:bg-violet-600 text-white border border-slate-700 hover:border-violet-600'}`} data-testid={`plan-select-${p.id}`}>
                  Testar 7 dias gratis
                </button>
              </motion.div>
            ))}
          </div>

          {/* ADD-ONS + calculadora */}
          <div className="mt-14 grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">Add-ons</p>
              <h3 className="mt-2 text-lg font-bold text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>Modulos a parte — ligue so o que o seu negocio usa</h3>
              <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="addons-grid">
                {addons.map(a => {
                  const on = selectedAddons.includes(a.id);
                  return (
                    <button key={a.id} type="button" onClick={() => toggleAddon(a.id)} className={`text-left rounded-2xl p-4 border transition-all ${on ? 'border-amber-400 bg-amber-400/10' : 'border-slate-800 bg-[#16162a] hover:border-slate-600'}`} data-testid={`addon-toggle-${a.id}`} aria-pressed={on}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-bold text-white">{a.name}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{a.description}</p>
                        </div>
                        <span className={`h-5 w-5 rounded-md border flex items-center justify-center flex-shrink-0 ${on ? 'bg-amber-400 border-amber-400 text-[#0f0f23]' : 'border-slate-600'}`}>{on && <Check size={14} />}</span>
                      </div>
                      <p className="mt-3 text-sm font-extrabold text-amber-300">+ {fmt(a.price)}<span className="text-slate-500 font-normal text-xs">/mes</span></p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="rounded-3xl border border-violet-500/30 bg-[#16162a] p-6 sticky top-24" data-testid="price-calculator">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-300">Simule sua mensalidade</p>
                <label className="block mt-4 text-sm text-slate-300">Filiais adicionais
                  <div className="mt-1 flex items-center gap-2">
                    <button type="button" onClick={() => setBranches(b => Math.max(0, b - 1))} className="h-9 w-9 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-bold" data-testid="calc-branch-minus">−</button>
                    <span className="w-10 text-center font-bold text-white" data-testid="calc-branch-count">{branches}</span>
                    <button type="button" onClick={() => setBranches(b => b + 1)} className="h-9 w-9 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-bold" data-testid="calc-branch-plus">+</button>
                    <span className="text-xs text-slate-500">× {fmt(branchPrice)}</span>
                  </div>
                </label>
                <div className="mt-5 space-y-2">
                  {mainPlans.map(p => {
                    const total = Number(p.price) + branches * Number(p.branch_price ?? branchPrice) + addonTotal;
                    return (
                      <button key={p.id} type="button" onClick={() => setRegister({ plan: p })} className="w-full flex items-center justify-between rounded-xl px-3 py-2.5 bg-[#0f0f23] border border-slate-800 hover:border-violet-500 transition-colors" data-testid={`calc-plan-${p.id}`}>
                        <span className="text-sm text-slate-300">{p.name}</span>
                        <span className="font-extrabold text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>{fmt(total)}<span className="text-xs text-slate-500 font-normal">/mes</span></span>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-[11px] text-slate-500">{selectedAddons.length} add-on(s) selecionado(s) · {fmt(addonTotal)}/mes</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section id="como-funciona" className="bg-[#f8fafc] text-slate-900 py-24">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-3 gap-8">
          {[
            [Zap, 'Escolha o plano', 'O plano define o tipo de negocio e libera automaticamente os modulos e permissoes certos para a sua operacao.'],
            [Building2, 'Cadastre a empresa', 'Em 2 minutos sua conta esta pronta, com link proprio (suaempresa.8ip) e 7 dias gratis.'],
            [Lock, 'Opere com seguranca', 'Perfis de acesso por usuario, auditoria de estoque e backups automaticos. Suporte humano para configurar o fiscal.'],
          ].map(([Icon, t, d], i) => (
            <div key={t} className="relative rounded-3xl bg-white border border-slate-200 p-7">
              <span className="absolute -top-4 left-7 h-8 w-8 rounded-full bg-violet-600 text-white text-sm font-black flex items-center justify-center">{i + 1}</span>
              <Icon className="text-violet-600" size={26} />
              <p className="mt-4 text-lg font-extrabold" style={{ fontFamily: 'Plus Jakarta Sans' }}>{t}</p>
              <p className="mt-2 text-sm text-slate-600">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ + CONTATO */}
      <section id="faq" className="py-24">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-400">Duvidas frequentes</p>
            <div className="mt-6 divide-y divide-slate-800" data-testid="faq-list">
              {FAQ.map(([q, a]) => (
                <details key={q} className="group py-4">
                  <summary className="cursor-pointer list-none flex items-center justify-between text-slate-100 font-semibold">{q}<ChevronRight size={18} className="text-slate-500 group-open:rotate-90 transition-transform" /></summary>
                  <p className="mt-2 text-sm text-slate-400">{a}</p>
                </details>
              ))}
            </div>
          </div>
          <div id="contato" className="lg:col-span-5 rounded-3xl bg-gradient-to-br from-violet-600 to-indigo-700 p-8 text-white shadow-2xl shadow-violet-800/40">
            <p className="text-2xl font-extrabold" style={{ fontFamily: 'Plus Jakarta Sans' }}>Quer uma demonstracao?</p>
            <p className="mt-2 text-violet-100 text-sm">Nosso consultor mostra o sistema rodando com o seu tipo de negocio e ajuda a escolher o plano certo.</p>
            <a href={consultor} target={consultor.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-violet-700 font-bold hover:bg-violet-50 transition-colors" data-testid="contact-consultor">
              <MessageCircle size={18} /> Falar com consultor
            </a>
            {branding?.email && <p className="mt-4 text-xs text-violet-200">{branding.email}</p>}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-5 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <Logo branding={branding} />
          <p>© {new Date().getFullYear()} {branding?.name || '8IP'} Tecnologia · Sistema de gestao e fiscal</p>
        </div>
      </footer>

      <AnimatePresence>
        {register && (
          <RegisterModal plan={register.plan} addons={addons} initialAddons={selectedAddons} branchPrice={branchPrice}
            onClose={() => setRegister(null)} navigate={navigate} />
        )}
      </AnimatePresence>
    </div>
  );
};

const RegisterModal = ({ plan, addons, initialAddons, branchPrice, onClose, navigate }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [chosen, setChosen] = useState(initialAddons || []);
  const [form, setForm] = useState({ store_name: '', owner_name: '', email: '', password: '', phone: '', subdomain: '' });
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const generateSlug = (name) => name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '').slice(0, 20);
  const checkSubdomain = async (slug) => {
    if (slug.length < 3) { setSubdomainAvailable(null); return; }
    try { const { data } = await axios.get(`${API}/resolve-tenant/${slug}`); setSubdomainAvailable(!data.id); }
    catch { setSubdomainAvailable(true); }
  };
  const handleStoreNameChange = (name) => { set('store_name', name); const slug = generateSlug(name); set('subdomain', slug); if (slug.length >= 3) checkSubdomain(slug); };
  const addonTotal = chosen.reduce((s, id) => s + (Number(addons.find(a => a.id === id)?.price) || 0), 0);
  const total = Number(plan.price) + addonTotal;
  const canGoStep2 = form.store_name && form.subdomain.length >= 3 && subdomainAvailable !== false;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/register`, { ...form, plan_id: plan.id, addon_plan_ids: chosen });
      toast.success('Conta criada! Bem-vindo ao 8IP.');
      navigate(`/${data.tenant.subdomain}/login`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar conta');
    } finally { setLoading(false); }
  };
  const inputCls = 'w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="register-modal">
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} className="bg-white text-slate-900 rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="p-6 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-violet-600">Plano {plan.name}</p>
            <h3 className="text-xl font-extrabold mt-1" style={{ fontFamily: 'Plus Jakarta Sans' }}>Crie sua conta — 7 dias gratis</h3>
            <p className="text-xs text-slate-500 mt-1">Tipo de negocio: <b className="text-slate-800" data-testid="register-business-type">{plan.business_type_name || plan.name}</b> <span className="text-slate-400">(definido pelo plano · troca via administracao)</span></p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="register-close"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {step === 1 && (
            <>
              <div>
                <label className="block text-sm font-semibold mb-1">Nome da empresa</label>
                <input value={form.store_name} onChange={e => handleStoreNameChange(e.target.value)} placeholder="Ex.: Mega Brasil Atacado" required className={inputCls} data-testid="register-store-name" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Endereco do sistema</label>
                <div className="flex items-center gap-2">
                  <input value={form.subdomain} onChange={e => { const s = generateSlug(e.target.value); set('subdomain', s); checkSubdomain(s); }} className={`${inputCls} font-mono`} data-testid="register-subdomain" />
                  <span className="text-xs text-slate-400 whitespace-nowrap">.8ip</span>
                </div>
                {subdomainAvailable === false && <p className="text-xs text-rose-600 mt-1">Endereco ja em uso</p>}
                {subdomainAvailable === true && <p className="text-xs text-emerald-600 mt-1">Disponivel</p>}
              </div>
              <div>
                <p className="text-sm font-semibold mb-2">Add-ons (opcional)</p>
                <div className="grid grid-cols-1 gap-2">
                  {addons.map(a => {
                    const on = chosen.includes(a.id);
                    return (
                      <label key={a.id} className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 cursor-pointer ${on ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`register-addon-${a.id}`}>
                        <span className="flex items-center gap-2 text-sm"><input type="checkbox" checked={on} onChange={() => setChosen(c => (on ? c.filter(x => x !== a.id) : [...c, a.id]))} className="accent-violet-600" />{a.name}</span>
                        <span className="text-sm font-bold text-violet-700">+ {fmt(a.price)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 flex items-center justify-between">
                <div className="text-xs text-slate-500">Mensalidade apos o periodo gratis<br /><span className="text-[11px]">+ {fmt(branchPrice)} por filial adicional</span></div>
                <p className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }} data-testid="register-total">{fmt(total)}<span className="text-sm text-slate-400 font-normal">/mes</span></p>
              </div>
              <button type="button" disabled={!canGoStep2} onClick={() => setStep(2)} className="w-full py-3.5 rounded-full bg-violet-600 hover:bg-violet-500 disabled:bg-slate-300 text-white font-bold flex items-center justify-center gap-2" data-testid="register-next">
                Continuar <ArrowRight size={18} />
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <div>
                <label className="block text-sm font-semibold mb-1">Seu nome</label>
                <input value={form.owner_name} onChange={e => set('owner_name', e.target.value)} required className={inputCls} data-testid="register-owner" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">E-mail</label>
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} required className={inputCls} data-testid="register-email" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">WhatsApp</label>
                <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(62) 99999-0000" className={inputCls} data-testid="register-phone" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Senha</label>
                <input type="password" value={form.password} onChange={e => set('password', e.target.value)} required minLength={6} className={inputCls} data-testid="register-password" />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setStep(1)} className="px-5 py-3.5 rounded-full border border-slate-300 font-semibold" data-testid="register-back">Voltar</button>
                <button type="submit" disabled={loading} className="flex-1 py-3.5 rounded-full bg-violet-600 hover:bg-violet-500 disabled:bg-slate-300 text-white font-bold" data-testid="register-submit">
                  {loading ? 'Criando...' : 'Criar minha conta'}
                </button>
              </div>
            </>
          )}
        </form>
      </motion.div>
    </motion.div>
  );
};

export default LandingPage;
